import * as THREE from "three";
import { MATS } from "../materials.js";
import * as H from "./spec.js";

/**
 * The covered front porch -- everything under and in front of the porch roof
 * (the roof itself belongs to shell.js): deck, columns, balustrade, steps,
 * soffit, and the two chairs from the photograph.
 *
 * This is also the cat's main way up the house, so every standing surface is
 * deliberate.  The cat jumps 0.95 m and steps 0.23 m; the chain it gets is:
 *
 *   grade 0.00 -> tread 1 0.184 -> tread 2 0.367 -> deck 0.55   (hops, 0.183 each)
 *   deck 0.55  -> chair seat 0.97 (+0.42) -> chair back 1.43 (+0.46)
 *              -> rail top 1.52 (+0.09) -> newel cap 1.64 (+0.12)
 *   perch:     grade -> left pier cap 0.82 (one jump, 0.82 < 0.95); the right
 *              pier carries the corner column now and is not standable
 *   west exit: the left end of the deck is open (the client had the
 *              left-of-door railing removed) -- sidepath.js puts side steps
 *              and the back-door path against the opening left here
 *
 * Rail top (1.52) to the porch roof (Y.porchEave 3.30) is 1.78 -- far past a
 * jump, and a porch has nothing honest at ~2.4 m to bridge it, so the porch
 * roof is deliberately NOT reachable from here.
 */

const P = H.PORCH;
const DECK = P.deck;                       // 0.55 -- one jump from grade
const MIDX = (P.minX + P.maxX) / 2;        // -2.275
const SPANX = P.maxX - P.minX;             // 6.45
const MIDZ = (H.FACE + P.front) / 2;       // 2.60
const SPAND = P.front - H.FACE;            // 2.20

const RAIL_TOP = P.railTop;                // 1.52: 0.97 above deck, just over
                                           // the 0.95 jump -- bridged by the
                                           // chair, NOT lowered (photo height)
const BOT_RAIL = DECK + 0.09;              // bottom rail underside
const BAL_LO = BOT_RAIL + 0.06;            // balusters run BAL_LO..TOP_RAIL_LO
const TOP_RAIL_LO = RAIL_TOP - 0.09;

const COL_Z = P.front - 0.18;              // column/rail line, 3.52
const COL_HALF = P.columnW / 2;
const CAP_TOP = H.Y.porchEave - 0.02;      // capitals tuck under the soffit

const RISE = DECK / P.steps;               // 0.1833 -- inside the cat's 0.23 m
                                           // step, so the flight is walked.
                                           // It was written when the step-up
                                           // was 0.13 and every tread had to
                                           // be hopped; player.js raised it to
                                           // 0.23 precisely so risers in this
                                           // 0.183-0.217 band walk, and these
                                           // are the treads it was measured on.
const STEP_W = 1.30;                       // matches WALK.width, centred on door
const SIDE_NEWEL_Z = 2.70;                 // head of the side steps
                                           // (sidepath.js): the left return
                                           // keeps only its corner half,
                                           // finished with a newel here

const TRIM = MATS[H.LOOK.trim];
const RAILM = MATS[H.LOOK.rail];

export function build(b) {
  deck(b);
  soffit(b);

  // Four bays across the front, plus a return pilaster at the left wall.
  // The three left supports are plain deck columns; the right corner one is
  // the pier-and-column stack beside the steps (pierColumn below).  No right
  // return: it would land 5 cm off the door surround (door edge x 0.525) and
  // the entry gable takes over structurally on that side anyway.
  const colXs = [];
  for (let i = 0; i < 4; i++) colXs.push(-5.32 + i * ((0.77 - -5.32) / 3));
  // Only the two outer bays are plain deck columns.  Both supports flanking
  // the steps are pier-and-column stacks, so the entry reads symmetrically;
  // the old plain column at -1.26 is gone, or it would have stood 0.43 m from
  // the left stack.
  for (const x of colXs.slice(0, 2)) column(b, x, COL_Z);
  column(b, colXs[0], H.FACE + 0.14);

  // Balustrade.  Everything left of the door came off at the client's ask,
  // except the far-left front bay: both its ends land on columns (nothing
  // reads sawn off) and it carries the chair -> rail climbing chain above.
  // The left side return is cut back to its corner half, finished with a
  // newel at the head of the side steps; the opening between that newel
  // (z 2.62) and the wall pilaster (z 1.79) is where the cat walks off the
  // deck onto sidepath.js's top tread.  The removed runs leave no colliders
  // behind -- railRun made its own, and those calls are simply gone.
  const f = COL_HALF;
  railRun(b, "x", colXs[0] + f, colXs[1] - f, COL_Z);
  railRun(b, "z", SIDE_NEWEL_Z + 0.07, COL_Z - f, colXs[0]);
  newel(b, colXs[0], SIDE_NEWEL_Z);

  steps(b);
  for (const s of [-1, 1]) pierColumn(b, s * (STEP_W / 2 + 0.18), 3.80);

  // The two chairs from the photo.  The nearer one doubles as the ladder to
  // the rail: seat 0.97 (+0.42 from deck), back 1.43 (+0.46), rail (+0.09).
  chair(b, -4.30, 2.90);
  chair(b, -2.35, 2.55);

  // Doormat -- coir-coloured planks material reads closest.
  b.slab(H.X.door, DECK + 0.002, H.FACE + 0.45, 0.95, 0.60,
         MATS.fence, { thickness: 0.016, tile: 0.35 });

  // Climbing-route rungs.
  b.mark("porch.tread1", 0, RISE, 4.15);
  b.mark("porch.tread2", 0, RISE * 2, 3.85);
  b.mark("porch.deck", 0, DECK, MIDZ);
  b.mark("porch.pierCap", -(STEP_W / 2 + 0.18), 0.82, 4.00);
  b.mark("porch.chairSeat", -4.30, DECK + 0.42, 2.90);
  b.mark("porch.chairBack", -4.30, DECK + 0.88, 2.71);
  b.mark("porch.railTop", -4.30, RAIL_TOP, COL_Z);
  b.mark("porch.pierCap", -(STEP_W / 2 + 0.18), 0.82, 3.80);
}

// ---------------------------------------------------------------------------
function deck(b) {
  // Brick base course, inset so the timber skirt above it overhangs.
  b.block(MIDX, 0, MIDZ - 0.025, SPANX - 0.10, 0.30, SPAND - 0.05,
          MATS[H.LOOK.brick]);

  // Skirt; the deck and base colliders already cover these faces.  Concrete,
  // not boards -- this is a poured porch slab on a brick base, not a deck.
  b.block(MIDX, 0.30, P.front - 0.04, SPANX, 0.22, 0.08, MATS.concreteDark, { solid: false });
  b.block(P.minX + 0.04, 0.30, MIDZ, 0.08, 0.22, SPAND, MATS.concreteDark, { solid: false });
  b.block(P.maxX - 0.04, 0.30, MIDZ, 0.08, 0.22, SPAND, MATS.concreteDark, { solid: false });

  // Slab with the outward fall: ~25 mm drop over the 2.2 m depth
  // (about 1%), pinned to 0.555 at the wall so the door threshold meets it.
  // The collider stays a flat box topping out at exactly DECK; the ~2 cm
  // visual difference at the front edge is invisible at cat scale.
  const fall = 0.025 / SPAND;
  const g = new THREE.BoxGeometry(SPANX, 0.05, SPAND + 0.01);
  g.rotateX(fall);
  g.translate(MIDX, DECK - 0.0325, MIDZ);
  b.add(g, MATS.concrete, {
    tile: 1.5,
    collider: { x: MIDX, y: 0.30, z: MIDZ, w: SPANX, h: DECK - 0.30, d: SPAND },
  });
}

function soffit(b) {
  // Cream ceiling so the underside of shell's porch roof never reads as sky.
  // Never touched by the cat -- the roof's own collider lives above it.
  b.block(MIDX, H.Y.porchEave - 0.06, MIDZ, SPANX, 0.06, SPAND,
          MATS[H.LOOK.trimShade], { solid: false });
}

function column(b, x, z) {
  const w = P.columnW;
  b.block(x, DECK, z, w + 0.12, 0.07, w + 0.12, TRIM, { solid: false });
  b.block(x, DECK + 0.07, z, w + 0.05, 0.08, w + 0.05, TRIM, { solid: false });
  b.block(x, DECK + 0.15, z, w, CAP_TOP - DECK - 0.29, w, TRIM, { solid: false });
  b.block(x, CAP_TOP - 0.14, z, w + 0.05, 0.07, w + 0.05, TRIM, { solid: false });
  b.block(x, CAP_TOP - 0.07, z, w + 0.12, 0.07, w + 0.12, TRIM, { solid: false });
  // One straight collider per column instead of five -- the moulding lips
  // would snag a cat brushing past.
  b.collider(x, DECK, z, w + 0.04, CAP_TOP - DECK, w + 0.04);
}

/**
 * One balustrade run between two supports, along "x" or "z".
 * A single full-height collider per run (top face exactly at RAIL_TOP) so the
 * cat lands on a clean beam instead of snagging on 37 invisible balusters.
 */
function railRun(b, axis, a0, a1, at) {
  const len = a1 - a0;
  if (len < 0.1) return;
  const mid = (a0 + a1) / 2;
  const box = (p, y, w, h, t) =>
    axis === "x"
      ? b.block(p, y, at, w, h, t, RAILM, { solid: false })
      : b.block(at, y, p, t, h, w, RAILM, { solid: false });

  box(mid, TOP_RAIL_LO, len, RAIL_TOP - TOP_RAIL_LO, 0.12);
  box(mid, BOT_RAIL, len, 0.06, 0.09);
  const n = Math.max(1, Math.round(len / 0.135) - 1);
  for (let i = 1; i <= n; i++) {
    box(a0 + (len * i) / (n + 1), BAL_LO, 0.045, TOP_RAIL_LO - BAL_LO, 0.045);
  }
  if (axis === "x") b.collider(mid, DECK, at, len, RAIL_TOP - DECK, 0.14);
  else b.collider(at, DECK, mid, 0.14, RAIL_TOP - DECK, len);
}

function newel(b, x, z) {
  // Cap tops out at 1.64: a 0.12 step up from the rail top -- an extra perch
  // beside the step opening.
  b.block(x, DECK, z, 0.14, RAIL_TOP - DECK + 0.07, 0.14, TRIM, { solid: false });
  b.block(x, RAIL_TOP + 0.07, z, 0.20, 0.05, 0.20, TRIM, { solid: false });
  b.collider(x, DECK, z, 0.16, RAIL_TOP + 0.12 - DECK, 0.16);
}

function steps(b) {
  // Two projecting treads; the deck edge is the third riser.  Bodies are
  // solid:false so each collider top matches the *nosing* surface, not the
  // body 3 cm below it.
  const dark = MATS.concreteDark;
  b.block(0, 0, 3.85, STEP_W, RISE * 2 - 0.032, 0.30, dark, { solid: false });
  b.block(0, 0, 4.15, STEP_W, RISE - 0.032, 0.30, dark, { solid: false });
  // Nosings: 3 cm concrete caps with a 25 mm lip.
  b.block(0, RISE * 2 - 0.032, 3.8625, STEP_W + 0.04, 0.032, 0.335,
          MATS.concrete, { solid: false });
  b.block(0, RISE - 0.032, 4.1625, STEP_W + 0.04, 0.032, 0.335,
          MATS.concrete, { solid: false });
  b.collider(0, 0, 3.85, STEP_W, RISE * 2, 0.30);   // tread top 0.367
  b.collider(0, 0, 4.15, STEP_W, RISE, 0.30);       // tread top 0.184
}
/**
 * The right corner support: the brick pier as the base and the white column
 * rising out of its cap, on one shared centre line -- a single element from
 * grade to the soffit.  (It used to be a free column at (0.77, 3.52) with
 * the pier standing separately in front of it at (0.83, 4.00), which read
 * as two overlapping objects.)  Sits at z 3.80 so the capital stays under
 * the porch roof, whose eave line is z 4.02.
 *
 * Two stacked colliders, not one: the pier is wider than the shaft, so its
 * cap really is a ledge, and a cat should be able to sit on it.  They are
 * contiguous, so brushing past still feels like one post.
 */
function pierColumn(b, x, z) {
  const w = P.columnW;
  b.block(x, 0, z, 0.36, 0.74, 0.60, MATS[H.LOOK.brick], { solid: false });
  b.block(x, 0.74, z, 0.44, 0.08, 0.68, MATS[H.LOOK.stone], { solid: false });
  // column base moulding sits on the pier cap; same profile as column()
  b.block(x, 0.82, z, w + 0.12, 0.07, w + 0.12, TRIM, { solid: false });
  b.block(x, 0.89, z, w + 0.05, 0.08, w + 0.05, TRIM, { solid: false });
  b.block(x, 0.97, z, w, CAP_TOP - 0.97 - 0.14, w, TRIM, { solid: false });
  b.block(x, CAP_TOP - 0.14, z, w + 0.05, 0.07, w + 0.05, TRIM, { solid: false });
  b.block(x, CAP_TOP - 0.07, z, w + 0.12, 0.07, w + 0.12, TRIM, { solid: false });
  b.collider(x, 0, z, 0.44, 0.82, 0.68);              // pier, cap at 0.82
  b.collider(x, 0.82, z, 0.40, CAP_TOP - 0.82, 0.40);  // shaft above it
}

function chair(b, x, z) {
  // Seat and back keep their own colliders -- they are ladder rungs
  // (deck +0.42 to seat, +0.46 to back top, +0.09 to the rail).
  const T = MATS.timber;
  for (const dx of [-0.20, 0.20]) {
    for (const dz of [-0.185, 0.185]) {
      b.block(x + dx, DECK, z + dz, 0.04, 0.37, 0.04, T, { solid: false });
    }
  }
  b.block(x, DECK + 0.37, z, 0.46, 0.05, 0.44, T);                     // seat 0.97
  b.block(x, DECK + 0.42, z - 0.195, 0.46, 0.46, 0.05, T);             // back 1.43
  for (const dx of [-0.215, 0.215]) {
    b.block(x + dx, DECK + 0.58, z - 0.02, 0.04, 0.04, 0.36, T, { solid: false });
  }
}
